import { Order, OrdersResponse, ORDER_STATUS } from 'types/api/order'
import { useQuery, useInfiniteQuery } from 'react-query'
import axios from 'lib/axiosConfig'
import { DeliveriesForPlace, DELIVERY_TYPE } from 'types/model/order'
import { DeliveriesResponse } from 'types/api/delivery'
import {
  getRouteNameInAlphabet,
  isNotBool,
  selectDeliveryAreaColor,
} from 'lib/utils'
import theme from 'lib/theme'
import { addDays, format } from 'date-fns'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import polyUtil from 'polyline-encoded'
import { useEffect } from 'react'
import { DeliveryShipment } from 'types/model/delivery'

export const useOrdersForPlace = (
  placeId: string,
  startDate: string,
  endDate: Date
) => {
  const ordersPayload = {
    place_id: placeId,
    start_delivery_date: startDate,
    end_delivery_date: format(endDate, 'yyyy-MM-dd'),
    page_size: 100,
    delivery_method_id: '494fe4ae-bc21-4b8e-a225-34e8eb16c79d',
  }

  const {
    data: orderData,
    hasNextPage,
    isFetchingNextPage,
    isFetching: ordersLoading,
    fetchNextPage,
  } = useInfiniteQuery(
    ['ordersForPlace', startDate],
    async ({ pageParam = 1 }) => {
      const { data } = await axios.get<OrdersResponse>(`/v1/orders`, {
        params: {
          ...ordersPayload,
          page: pageParam,
        },
      })

      // Get orders that are for home delivery and have latitude and longitude in the address
      const orderData = data.data.filter((order) =>
        order.parts.some((part) => {
          return (
            part.deliveryMethod.service.type === DELIVERY_TYPE.HOME_DELIVERY &&
            Boolean(part.deliveryMethod.service.address.latitude) &&
            Boolean(part.deliveryMethod.service.address.longitude)
          )
        })
      )
      return {
        meta: data.meta,
        orders: orderData,
      }
    },
    {
      getNextPageParam: (lastPage) => lastPage.meta.pagination.nextPage,
    }
  )

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage])

  const { data: deliveriesFromApi, isFetching: deliveriesLoading } = useQuery(
    ['deliveries', startDate],
    async () => {
      const deliveriesPayload = {
        start_date: startDate,
        end_date: format(addDays(endDate, 1), 'yyyy-MM-dd'),
        page_size: 500,
      }

      const { data } = await axios.get<DeliveriesResponse>(
        `/v1/dts/places/${placeId}/deliveries`,
        { params: deliveriesPayload }
      )
      return data
    }
  )

  const completeOrders = orderData?.pages.flatMap((data) => data.orders) || []

  // split orders so each order has one part, check if order part belongs to selected store
  // from here on, part Id may sometimes be referred to as order Id in route planning, but routes are handled in order parts, not orders!
  const orders = completeOrders.reduce((acc, order) => {
    if (order.parts?.length > 1) {
      for (let i = 0; i < order.parts.length; i += 1) {
        if (order.parts[i].placeId === placeId) {
          acc.push({ ...order, parts: [order.parts[i]] })
        }
      }
    } else {
      acc.push(order)
    }
    return acc
  }, [] as Order[])

  let routeIndex = 1
  let colorIndex = 0
  if (!hasNextPage && !isFetchingNextPage) {
    const shipmentsWithOrders =
      deliveriesFromApi?.data
        .filter(
          (delivery) =>
            delivery.shipments.length > 0 &&
            delivery.date === format(endDate, 'yyyy-MM-dd')
        )
        .map((delivery) => ({
          ...delivery,
          shipments: delivery.shipments.map((shipment) => {
            return {
              ...shipment,
              masterShipmentId: delivery.id,
              masterShipmentCrc: delivery.crc,
              // this is different from the delivery start time in the api, it comes from the shipment start time that shows when manually set
              // deliveryStartTime, shipmentStartTime, and shipmentEndTime are in store time zone
              deliveryStartTime:
                shipment.shipmentRoute?.length > 0 && shipment.startTime
                  ? new Date(shipment.startTime).toUTCString()
                  : null,
              shipmentStartTime:
                shipment.shipmentRoute?.length > 0 &&
                shipment.shipmentRoute[0] !== null
                  ? shipment.shipmentRoute[0].startAt
                  : null,
              shipmentEndTime:
                shipment.shipmentRoute?.length > 0 &&
                shipment.shipmentRoute[0] !== null
                  ? shipment.shipmentRoute[0].finishAt
                  : null,
              date: delivery.date,
            }
          }),
        }))
        .flatMap((delivery) =>
          delivery.shipments.filter((shipment) => Boolean(shipment.groupIndex))
        )
        .filter(
          (delivery, index, self) =>
            index === self.findIndex((t) => t.id === delivery.id)
        ) || []

    const deliveries: DeliveryShipment[] = shipmentsWithOrders
      .sort((a, b) => a.id - b.id)
      .map((shipment) => {
        const { shipmentOrders = [], shipmentRoute = [], groupIndex } = shipment
        if (groupIndex === -1) {
          return false
        }
        const statuses = shipmentOrders.map((shipment) => {
          const ordersInShipment =
            orders.filter(
              (order) =>
                order.parts[0].id.toString() === shipment.orderId.toString()
            ) || []
          return ordersInShipment.map((order) => order.parts[0].status).join()
        }) as ORDER_STATUS[]

        const orderStatus = () => {
          if (statuses.some((status) => status === ORDER_STATUS.DELIVERING)) {
            return ORDER_STATUS.DELIVERING
          }
          if (statuses.every((status) => status === ORDER_STATUS.CANCELLED)) {
            return ORDER_STATUS.CANCELLED
          }
          if (
            statuses.every(
              (status) =>
                status === ORDER_STATUS.DELIVERED ||
                status === ORDER_STATUS.CANCELLED
            )
          ) {
            return ORDER_STATUS.DELIVERED
          }
          return ORDER_STATUS.COLLECTED
        }

        // shipment is in planning when there is an order_id in the shipmentOrders
        const routeIsInPlanning =
          groupIndex !== -1 &&
          shipmentOrders.some((order) => Boolean(order.orderId))

        const routePolygons =
          shipmentRoute?.flatMap((route) => {
            return Boolean(route)
              ? route?.steps?.map((step) => polyUtil.decode(step.points))
              : []
          }) || []

        // calculate midpoint coordinate of each route
        const midPoints = shipmentRoute?.map((route) => {
          const halfDistance = Boolean(route) ? route.distance / 2 : null
          if (!halfDistance) return null
          // find which step halfway point is in
          let distance = 0
          let oldDistance = 0
          const index = route.steps.findIndex((step) => {
            oldDistance = distance
            distance += step.distance
            return distance > halfDistance
          })
          if (index === -1) return null

          const listOfCoordinates = polyUtil.decode(route.steps[index].points)
          const ratio = (halfDistance - oldDistance) / (distance - oldDistance)
          if (listOfCoordinates.length < 1) {
            return [route.steps[index].startLat, route.steps[index].startLon]
          }

          const midPoint =
            listOfCoordinates[Math.floor(listOfCoordinates.length * ratio)]

          return midPoint
        })

        // array of order partIDs found in the shipment
        const orderIds = shipmentOrders.map((shipmentOrder) =>
          shipmentOrder.orderId.toString()
        )

        const color = routeIsInPlanning
          ? selectDeliveryAreaColor(colorIndex)
          : theme.palette.text.primary

        const routeName = getRouteNameInAlphabet(shipment.groupIndex)

        if (routeIsInPlanning) {
          colorIndex += 1
        }

        return {
          ...shipment,
          orderIds,
          routeIsInPlanning,
          color,
          routeName,
          deliveryCrc: shipment.masterShipmentCrc,
          routePolygons,
          midPoints,
          orderStatus: orderStatus(),
        }
      })
      .filter(isNotBool)

    if (orders && !ordersLoading && !deliveriesLoading) {
      const parsedDeliveries =
        deliveries && deliveries.length > 0
          ? deliveries
              .reverse()
              .filter((shipment) =>
                orders.some((order) =>
                  shipment.orderIds.includes(order.parts[0].id)
                )
              )
          : []

      const combinedData = orders
        .reverse()
        .map((order) => {
          const deliveriesWithExistingOrderIds =
            deliveries && deliveries.length > 0
              ? deliveries.filter((shipment) =>
                  shipment.orderIds.includes(order.parts[0].id)
                )
              : []
          const shipmentData =
            deliveriesWithExistingOrderIds.length > 0
              ? deliveriesWithExistingOrderIds.map((shipment) => {
                  return {
                    deliveryCrc: Number(shipment.masterShipmentCrc),
                    routeName: shipment.routeIsInPlanning
                      ? shipment.routeName
                      : routeIndex.toString(),
                    routePolygons: shipment.routePolygons,
                    shipmentDeliveryTimes: {
                      startTime: shipment.shipmentStartTime || null,
                      endTime: shipment.shipmentEndTime || null,
                      deliveryDate: shipment.date,
                      shipmentStartTime:
                        shipment.deliveryStartTime?.substring(17, 22) ||
                        order.parts[0].deliveryMethod.service.deliverySlot
                          .startTime,
                    },
                    orderStatus: shipment.orderStatus,
                  }
                })[0]
              : { routeName: routeIndex.toString() }

          const routeIsInPlanning = deliveriesWithExistingOrderIds.map(
            (shipment) => shipment.routeIsInPlanning
          )[0]

          const color =
            deliveriesWithExistingOrderIds.map(
              (shipment) => shipment.color
            )[0] || theme.palette.text.primary

          if (!routeIsInPlanning) {
            routeIndex += 1
          }
          return {
            ...order,
            orderDeliveryTimes: {
              startTime:
                order.parts[0].deliveryMethod.service.deliverySlot.startTime,
              endTime:
                order.parts[0].deliveryMethod.service.deliverySlot.endTime,
              deliveryDate:
                order.parts[0].deliveryMethod.service.deliverySlot.deliveryDate.slice(
                  0,
                  10
                ),
            },
            shipmentData: deliveriesWithExistingOrderIds,
            routeIsInPlanning,
            color,
            ...shipmentData,
          }
        })
        .filter(
          (order) =>
            order.parts[0].status !== ORDER_STATUS.CANCELLED ||
            order.routeIsInPlanning
        ) as DeliveriesForPlace[]

      return {
        ordersLoading,
        deliveriesLoading,
        combinedData,
        orders,
        deliveries: parsedDeliveries,
        deliveriesFromApi: deliveriesFromApi?.data || [],
        ordersFromnApi: orderData?.pages || [],
      }
    }
  }

  return {
    ordersLoading,
    deliveriesLoading,
    hasNextPage,
    orders,
    deliveriesFromApi: deliveriesFromApi?.data || [],
  }
}
